import React from "react";
import PropTypes from "prop-types";

import {
  Dialog,
  Grid,
  IconButton,
  makeStyles,
  Paper,
  Typography,
  withStyles,
} from "@material-ui/core";
import MuiDialogTitle from "@material-ui/core/DialogTitle";
import CloseIcon from "@material-ui/icons/Close";

import ContractButton from "./ContractButton";

const styles = (theme) => ({
  paperContainer: {
    borderRadius: theme.spacing(2),
    width: "100%",
    height: "200px",
    padding: theme.spacing(2),
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  dialogContainer: {
    borderRadius: theme.spacing(2),
  },
  titleSection: {
    padding: theme.spacing(2),
  },
  titleText: {
    alignSelf: "center",
  },
  closeButton: {
    padding: 0,
  },
  hr: {
    margin: 0,
    color: theme.palette.grey[400],
  },
  address: {
    borderRadius: "20px",
    paddingBottom: theme.spacing(2),
  },
  searchIcon: {
    color: theme.palette.grey[400],
  },
  coinList: {
    maxHeight: "500px",
    overflowY: "auto",
    padding: theme.spacing(2),
    "&::-webkit-scrollbar": {
      width: "4px",
    },
    "&::-webkit-scrollbar-track": {
      background: theme.palette.background.default,
      width: "2px",
    },
    "&::-webkit-scrollbar-thumb": {
      background: theme.palette.grey[400],
      borderRadius: "30px",
      width: "4px",
    },
  },
  coinContainer: {
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
  },
  ul: {
    listStyle: "none",
    display: "flex",
    alignItems: "center",
    gap: "10px",
    backgroundColor: theme.palette.background.default,
    borderRadius: "16px",
    padding: "2px",
    margin: "0",
    maxWidth: "min-content",
    marginLeft: theme.spacing(1),
  },
  li: {
    cursor: "pointer",
    display: "flex",
    flexFlow: "row nowrap",
    borderRadius: "14px",
    outline: "none",
    textDecoration: "none",
    fontSize: ".8rem",
    fontWeight: "500",
    padding: "9px 13px",
    wordBreak: "break-word",
    overflow: "hidden",
    whiteSpace: "nowrap",
    "&.active": {
      backgroundColor: theme.palette.background.paper,
    },
  },
  itemsListBox: {
    gap: theme.spacing(2),
  },
});

const useStyles = makeStyles(styles);

// This is a modified version of MaterialUI's DialogTitle component, I've added a close button in the top right corner
const DialogTitle = withStyles(styles)((props) => {
  const { children, classes, onClose, ...other } = props;
  return (
    <MuiDialogTitle
      disableTypography
      className={classes.titleSection}
      {...other}
    >
      <Grid
        container
        direction="row"
        justifyContent="space-between"
        alignContent="center"
      >
        <Typography variant="body1" className={classes.titleText}>
          {children}
        </Typography>

        {onClose ? (
          <IconButton
            aria-label="close"
            onClick={onClose}
            className={classes.closeButton}
          >
            <CloseIcon />
          </IconButton>
        ) : null}
      </Grid>
    </MuiDialogTitle>
  );
});

export default function InvestDialog({
  onClose,
  open,
  investContracts,
  account,
  network,
}) {
  // The CoinDialog component will display a dialog window on top of the page, allowing a user to select a coin
  // from a list (list can be found in 'src/constants/coins.js') or enter an address into a search field. Any entered
  // addresses will first be validated to make sure they exist.
  // When the dialog closes, it will call the `onClose` prop with 1 argument which will either be undefined (if the
  // user closes the dialog without selecting anything), or will be a string containing the address of a coin.

  const classes = useStyles();

  // Resets any fields in the dialog (in case it's opened in the future) and calls the `onClose` prop
  const exit = (info) => {
    onClose(info);
  };

  return (
    <Dialog
      open={open}
      onClose={() => exit(undefined)}
      fullWidth
      maxWidth="xs"
      classes={{ paper: classes.dialogContainer }}
    >
      <DialogTitle onClose={() => exit(undefined)}>Select Contract</DialogTitle>

      <hr className={classes.hr} />

      <div className="position-relative">
        <Grid container direction="column">
          <Grid item className={classes.coinList}>
            <Grid container direction="column" className={classes.itemsListBox}>
              {/* Maps all of the tokens in the constants file to buttons */}

              {investContracts.length ? (
                <>
                  {investContracts.map((info) => (
                    <Grid item key={info.contractAddress} xs={12}>
                      <ContractButton
                        info={info}
                        network={network}
                        onClick={(info) => exit(info)}
                      />
                    </Grid>
                  ))}
                </>
              ) : (
                <Paper elevation={0} className={classes.paperContainer}>
                  <p className="mb-0">There is no active pool</p>
                </Paper>
              )}
            </Grid>
          </Grid>
        </Grid>
      </div>
    </Dialog>
  );
}

InvestDialog.propTypes = {
  onClose: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
};
